// #2 Plano de Investigação (FP-CMP-010) — espelha as 9 seções do documento
// oficial. Diferente dos 5 do Pass 1, o corpo (hipóteses, fontes, pessoas,
// recursos, cronograma) é CAPTURADO na tela (efêmero, não persiste — opção c).
// Cabeçalho + seções fixas (Finalidade, Medidas de proteção) são automáticos.
//
// Fora do registry dos 5: precisa de `PlanoData` além do FormInput.
// Checkboxes: as fontes core do jsPDF não têm ☐ (U+2610) → usamos [ ]/[X] ASCII.

import type { FormBlock } from './form-pdf.js';
import { manualField } from './form-pdf.js';
import {
  type FormInput,
  categoriaLabel,
  gravidadeLabel,
  fmtDate,
  memberLabel,
  getTriagem,
} from './forms-common.js';

/** Seção 4 — fontes de informação (checkboxes marcados na tela). */
export interface PlanoFontes {
  documentosFisicos: boolean;
  registrosDigitais: boolean;
  logsAcessos: boolean;
  imagensGravacoes: boolean;
  anexosDenunciante: boolean;
  outros: boolean;
  outrosTexto: string;
}

/** Seção 5 — linha da tabela de pessoas a ouvir (texto livre). */
export interface PlanoPessoa {
  nomeFuncao: string;
  papel: string;
  statusOitiva: string;
}

/** Campos CAPTURADOS na tela (efêmeros). O resto é metadado ou texto fixo. */
export interface PlanoData {
  /** Seção 3. */
  hipoteses: string;
  /** Seção 4. */
  fontes: PlanoFontes;
  /** Seção 5. */
  pessoas: ReadonlyArray<PlanoPessoa>;
  /** Seção 6. */
  recursos: string;
  /** Seção 8. */
  inicioPrevisto: string;
  conclusaoPrevista: string;
  /** Seção 2 — data de elaboração (epoch-ms, setado no momento de gerar). */
  dataElaboracao: number;
}

const cb = (checked: boolean): string => (checked ? '[X]' : '[ ]');

export function buildPlano(input: FormInput, plano: PlanoData): FormBlock[] {
  const triagem = getTriagem(input.state);
  const responsavel = triagem ? memberLabel(input.members, triagem.classifiedBy) : '';
  const criticidade = gravidadeLabel(triagem?.gravidade);

  const blocks: FormBlock[] = [
    { kind: 'title', text: 'Plano de Investigação' },
    { kind: 'field', label: 'Código:', value: 'FP-CMP-010' },

    // 1. Finalidade — texto fixo do documento.
    { kind: 'section', text: '1. Finalidade' },
    {
      kind: 'paragraph',
      text:
        'Este plano orienta a condução da investigação interna de forma objetiva, técnica e ' +
        'imparcial, definindo hipóteses, fontes, pessoas a ouvir e recursos necessários, antes ' +
        'do início da coleta de evidências. Deve ser validado pelo Compliance Officer.',
    },

    // 2. Identificação — metadados automáticos do caso.
    { kind: 'section', text: '2. Identificação' },
    { kind: 'field', label: 'Protocolo da denúncia:', value: input.caseId },
    { kind: 'field', label: 'Número da investigação:', value: input.caseId },
    {
      kind: 'field',
      label: 'Categoria / criticidade:',
      value: criticidade
        ? `${categoriaLabel(input.categoria)} / ${criticidade}`
        : categoriaLabel(input.categoria),
    },
    {
      kind: 'field',
      label: 'Responsável pela apuração:',
      value: responsavel,
    },
    {
      kind: 'field',
      label: 'Data de elaboração do plano:',
      value: fmtDate(plano.dataElaboracao),
    },

    // 3. Hipóteses de trabalho — capturado.
    { kind: 'section', text: '3. Hipóteses de trabalho' },
  ];

  if (plano.hipoteses.trim() === '') {
    blocks.push(manualField('Hipóteses a verificar (sem prejulgamento):'));
  } else {
    blocks.push({ kind: 'paragraph', text: plano.hipoteses });
  }

  // 4. Fontes de informação — checkboxes.
  blocks.push(
    { kind: 'section', text: '4. Fontes de informação' },
    { kind: 'paragraph', text: 'Marque as fontes que serão analisadas:' },
    { kind: 'paragraph', text: `${cb(plano.fontes.documentosFisicos)} Documentos físicos` },
    {
      kind: 'paragraph',
      text: `${cb(plano.fontes.registrosDigitais)} Registros digitais / e-mails corporativos`,
    },
    {
      kind: 'paragraph',
      text: `${cb(plano.fontes.logsAcessos)} Logs de sistema e registros de acesso`,
    },
    {
      kind: 'paragraph',
      text: `${cb(plano.fontes.imagensGravacoes)} Imagens / gravações de segurança`,
    },
    {
      kind: 'paragraph',
      text: `${cb(plano.fontes.anexosDenunciante)} Anexos enviados pelo denunciante`,
    },
    {
      kind: 'paragraph',
      text: `${cb(plano.fontes.outros)} Outros: ${plano.fontes.outros ? plano.fontes.outrosTexto : ''}`.trimEnd(),
    },
  );

  // 5. Pessoas a serem ouvidas — tabela.
  blocks.push({ kind: 'section', text: '5. Pessoas a serem ouvidas' });
  const pessoasPreenchidas = plano.pessoas.filter(
    (p) => p.nomeFuncao.trim() !== '' || p.papel.trim() !== '' || p.statusOitiva.trim() !== '',
  );
  if (pessoasPreenchidas.length === 0) {
    blocks.push(manualField('Pessoas a ouvir (nome/função, papel, status):'));
  } else {
    blocks.push({
      kind: 'table',
      headers: ['Nome / função', 'Papel (envolvido/testemunha)', 'Status da oitiva'],
      rows: pessoasPreenchidas.map((p) => [p.nomeFuncao, p.papel, p.statusOitiva]),
    });
  }
  blocks.push({
    kind: 'paragraph',
    text: 'As oitivas seguem o FP-CMP-007 (Termo de Declaração).',
  });

  // 6. Recursos necessários — capturado.
  blocks.push({ kind: 'section', text: '6. Recursos necessários' });
  if (plano.recursos.trim() === '') {
    blocks.push(manualField('Recursos necessários:'));
  } else {
    blocks.push({ kind: 'paragraph', text: plano.recursos });
  }

  // 7. Medidas de proteção — texto fixo (3 itens).
  blocks.push(
    { kind: 'section', text: '7. Medidas de proteção' },
    {
      kind: 'paragraph',
      text: '- Proteção do denunciante e testemunhas contra retaliação (LGP-02-0.1);',
    },
    {
      kind: 'paragraph',
      text: '- Garantia da integridade e cadeia de custódia das evidências (FP-CMP-011);',
    },
    {
      kind: 'paragraph',
      text: '- Preservação do sigilo e da imparcialidade durante toda a apuração.',
    },
  );

  // 8. Cronograma estimado — capturado.
  blocks.push(
    { kind: 'section', text: '8. Cronograma estimado' },
    { kind: 'field', label: 'Início previsto:', value: plano.inicioPrevisto },
    { kind: 'field', label: 'Conclusão prevista:', value: plano.conclusaoPrevista },
  );

  // 9. Validação — assinatura do Compliance Officer.
  blocks.push(
    { kind: 'section', text: '9. Validação' },
    { kind: 'spacer', mm: 6 },
    { kind: 'signature', nome: '', papel: 'Compliance Officer — validação do plano' },
  );

  return blocks;
}
